import React from 'react';
import { Stepper, Step, StepButton, CircularProgress } from '@material-ui/core';
import { ChevronLeft } from '@material-ui/icons';
import { PrimaryButton, SecondaryButton } from '../../components/AtomComponents';
import './PxwConnectorConfig.scss';
import TenantStep from './TenantStep';
import VanillaSettingsStep from './VanillaSettingsStep';
import DeployBuildStep from './DeployBuildStep';
import QuickBooksStep from './QuickBooksStep';
import ShipStationStep from './ShipStationStep';
import DiagnosticStep from './DiagnosticStep';
import ReviewRunStep from './ReviewRunStep';
import usePxwConnectorConfig, { STEP_COUNT } from './usePxwConnectorConfig';
import { STEP_LABELS } from './types';

const PxwConnectorConfig = () => {
  const { formData, setFormData, nav, tasks } = usePxwConnectorConfig();
  const isLastStep = nav.processState === STEP_COUNT - 1;
  const isDeployStep = nav.processState === 2;
  const isDeployDone = tasks.statuses['vanilla'] === 'success' && tasks.statuses['deploy-build'] === 'success';

  const isSubmitDisabled = isLastStep ? tasks.isRunningAll : isDeployStep ? !isDeployDone : !nav.isCurrentStepValid();

  const renderCurrentStep = () => {
    switch (nav.processState) {
      case 0: return <TenantStep data={formData.tenant} onChange={setFormData.setTenant} />;
      case 1: return <VanillaSettingsStep data={formData.vanilla} onChange={setFormData.setVanilla} />;
      case 2: return <DeployBuildStep taskStatuses={tasks.statuses} taskErrors={tasks.errors} onRunTask={tasks.runSingleTask} />;
      case 3:
        return <QuickBooksStep data={formData.quickBooks} onChange={setFormData.setQuickBooks} enabled={formData.tenant.configureQuickBooks} onToggleEnabled={(enabled) => setFormData.setTenant({ ...formData.tenant, configureQuickBooks: enabled })} />;
      case 4:
        return <ShipStationStep data={formData.shipStation} onChange={setFormData.setShipStation} enabled={formData.tenant.configureShipStation} onToggleEnabled={(enabled) => setFormData.setTenant({ ...formData.tenant, configureShipStation: enabled })} />;
      case 5: return <DiagnosticStep data={formData.diagnostic} onChange={setFormData.setDiagnostic} />;
      case 6: return <ReviewRunStep taskStatuses={tasks.statuses} taskErrors={tasks.errors} onRunTask={tasks.runSingleTask} tenantData={formData.tenant} />;
      default: return null;
    }
  };

  const renderSubmitButtonContent = () => {
    if (!isLastStep) return 'Continue';
    if (tasks.isRunningAll) return <><CircularProgress size={14} className="pxw-btn-spinner" /> Running onboarding...</>;
    return 'Run onboarding';
  };

  const renderBackButton = () =>
    nav.processState === 0 ? <SecondaryButton onClick={nav.restart}>Cancel</SecondaryButton> : <SecondaryButton onClick={nav.goToPrevStep}>Back</SecondaryButton>;

  return (
    <div className="px-pxw-connector grid-x">
      <h3 className="small-12 pxw-header-heading">
        <ChevronLeft className="pxw-header-icon" onClick={nav.goToPrevStep} />
        <span>PXW Connectors Onboarding</span>
      </h3>
      <div className="small-12 margin-bottom-3">
        <section className="grid-x">
          <div className="small-12">
            <Stepper alternativeLabel activeStep={nav.processState}>
              {STEP_LABELS.map((label, index) => {
                const isStepDisabled = index > 2 && !isDeployDone;
                return (
                  <Step key={label} disabled={isStepDisabled}>
                    <StepButton disabled={isStepDisabled} onClick={() => nav.goToStep(index)}>{label}</StepButton>
                  </Step>
                );
              })}
            </Stepper>
          </div>

          {renderCurrentStep()}

          <div className="pxw-action-bar small-12 margin-top-3 margin-bottom-3">
            {renderBackButton()}
            <PrimaryButton onClick={isLastStep ? tasks.runAllTasks : nav.goToNextStep} disabled={isSubmitDisabled}>
              {renderSubmitButtonContent()}
            </PrimaryButton>
          </div>
        </section>
      </div>
    </div>
  );
};

export default PxwConnectorConfig;
